/**
 * Persists a Newsletter promo code locally so it survives navigation between
 * PDPs and so it can be re-attached to the next Add-to-Cart action.
 *
 * Why we need this: Shopware drops a promotion line item the moment its cart
 * becomes empty (or starts empty). Storing the code and re-applying it
 * together with the first product purchase keeps the discount eligible.
 *
 * The persisted record also carries the *scope* the Newsletter intended
 * (?prsc=p|d|s) plus the anchor id of the PDP it was captured on, and the
 * *apply mode* (?pra=add|replace). These let usePromoCode decide whether to
 * render the banner on subsequent PDPs and let the cart layer decide whether
 * to replace existing manual codes at add-to-cart time.
 */

// MERGE NOTE (read before resolving a conflict in this file):
// This file was rewritten from the old v2 (localStorage + 7-day TTL) version on two branches, so a
// conflict here is expected. The two variants are:
//   * ORT-3088 branch:  sessionStorage + v3 key; record = { code, scope, anchor, applyMode }.
//   * big branch #1354: the SAME, plus the optional ORT-3077 fields oNumbers + suppressOffers.
// The big branch is a strict SUPERSET. Resolve by KEEPING the big branch version so the ORT-3077
// fields survive. Every other line is kept byte-identical across both branches on purpose, so the
// only genuine difference is those two fields, fenced with "ORT-3077 fields" markers below.

// v3 (ORT-3088): session-scoped storage in sessionStorage, no TTL. The Newsletter team asked for
// the code to live only for the current browser session, not the previous 7-day window. v2
// (localStorage, 7-day TTL) is no longer read; those entries expire on their own.
const KEY = 'orthomol_promo_v3';

// Storage lives in sessionStorage (ORT-3088): the promo is remembered only for the current browser
// session, not the previous 7-day window. Wrapped so a missing or throwing sessionStorage (SSR,
// private mode, quota) degrades to a no-op instead of breaking the page.
const storage = (): Storage | null => {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

export type PromoScope = 'p' | 'd' | 's';
export type PromoApplyMode = 'add' | 'replace';

export interface PromoStorageRecord {
  code: string;
  scope: PromoScope;
  // Anchor identifies the PDP the link pointed to. Interpretation depends on scope:
  //   p → corporate product id (Pimcore numeric id)
  //   d → dosage form id (Pimcore numeric id)
  //   s → Shopware product id (unique packsize variant; UUID)
  // null when we couldn't determine the anchor at capture time (e.g. the link pointed to a
  // non-PDP, or JSON not yet hydrated). Then scope-gating falls back to oNumbers / stays visible.
  anchor: string | null;
  applyMode: PromoApplyMode;
  // --- ORT-3077 fields: big branch #1354 only; the ORT-3088 branch omits them by design. ---
  // MERGE: keep the optional oNumbers + suppressOffers that the big branch adds directly below.
  // ORT-3077 A: explicit o-numbers the code is valid for (from ?pron=). Used to scope the
  // banner/eligibility when the newsletter linked to a non-PDP (no product context to infer).
  oNumbers?: string[] | null;
  // ORT-3077 B: when true (?prooff=0), suppress actively offered Stage-2 promotions while this
  // newsletter code is in play, so it is not drowned out by competing auto-offers.
  suppressOffers?: boolean;
}

export function setPromoRecord(record: PromoStorageRecord): void {
  const s = storage();
  if (!s) return;
  try {
    s.setItem(KEY, JSON.stringify(record));
  } catch {
    // storage unavailable (private mode, quota, …) — best-effort, ignore.
  }
}

export function getPromoRecord(): PromoStorageRecord | null {
  const s = storage();
  if (!s) return null;
  try {
    const raw = s.getItem(KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as Partial<PromoStorageRecord>;
    // Guard against a malformed/foreign entry under our key: require code to be a non-empty string.
    if (typeof data?.code !== 'string' || !data.code) {
      s.removeItem(KEY);
      return null;
    }
    return {
      code: data.code,
      scope: data.scope === 'p' || data.scope === 'd' || data.scope === 's' ? data.scope : 'p',
      anchor: typeof data.anchor === 'string' ? data.anchor : null,
      applyMode: data.applyMode === 'replace' ? 'replace' : 'add',
      // --- ORT-3077 fields: big branch #1354 only; the ORT-3088 branch omits them by design. ---
      // MERGE: keep the oNumbers + suppressOffers that the big branch parses directly below.
      oNumbers: Array.isArray(data.oNumbers) ? data.oNumbers.filter((n): n is string => typeof n === 'string') : null,
      suppressOffers: data.suppressOffers === true,
    };
  } catch {
    return null;
  }
}

/** Convenience for callers that only care about the code (e.g. cart-add). */
export function getPromoCode(): string | null {
  return getPromoRecord()?.code ?? null;
}

export function clearPromoCode(): void {
  const s = storage();
  if (!s) return;
  try {
    s.removeItem(KEY);
  } catch {
    // ignore
  }
}
